import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { adminAPI } from '../services/api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

// Helper function to format time in minutes
const formatTime = (minutes) => {
  if (!minutes || minutes === 0) return '0 minutes';
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hours > 0 && mins > 0) {
    return `${hours}h ${mins}m`;
  } else if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  } else {
    return `${mins} minute${mins > 1 ? 's' : ''}`;
  }
};

const AdminReports = () => {
  const [statistics, setStatistics] = useState(null);
  const [userReport, setUserReport] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [tasksResolvedByUser, setTasksResolvedByUser] = useState([]);
  const [timeSpentByUser, setTimeSpentByUser] = useState([]);
  const [timeSpentByTaskType, setTimeSpentByTaskType] = useState([]);
  const [userProductivity, setUserProductivity] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    userId: '',
  });

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      const [
        stats,
        users,
        timelineData,
        resolvedByUser,
        timeByUser,
        timeByTaskType,
        productivity
      ] = await Promise.all([
        adminAPI.getStatistics(),
        adminAPI.getUserReport(),
        adminAPI.getTimeline(filters),
        adminAPI.getTasksResolvedByUser(filters),
        adminAPI.getTimeSpentByUser(filters),
        adminAPI.getTimeSpentByTaskType(filters),
        adminAPI.getUserProductivityMetrics(filters),
      ]);
      setStatistics(stats.data);
      setUserReport(users.data || []);
      setTimeline(timelineData.data || []);
      setTasksResolvedByUser(resolvedByUser.data || []);
      setTimeSpentByUser(timeByUser.data || []);
      setTimeSpentByTaskType(timeByTaskType.data || []);
      setUserProductivity(productivity.data || []);
    } catch (err) {
      console.error('Error loading reports:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleExport = async (format) => {
    try {
      const response = await adminAPI.exportReport(format, filters);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert('Failed to export report');
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading reports...</div>;
  }

  const statusData = statistics
    ? [
        { name: 'Pending', value: statistics.pending || 0 },
        { name: 'In Progress', value: statistics.in_progress || 0 },
        { name: 'Completed', value: statistics.completed || 0 },
      ]
    : [];

  const userActivityData = userReport.map((user) => ({
    name: user.username,
    created: user.tasks_created || 0,
    completed: user.tasks_completed || 0,
  }));

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'user-performance', label: 'User Performance' },
    { id: 'time-analytics', label: 'Time Analytics' },
    { id: 'task-type-analytics', label: 'Task Type Analytics' },
  ];

  return (
    <div className="main-content">
      <div className="page-header">
        <h1 className="page-title">Reports & Analytics</h1>
        <p className="page-subtitle">Comprehensive reports and analytics dashboard</p>
      </div>

      <div className="card">
        <div style={styles.filters}>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            className="form-input"
            style={styles.filterInput}
            placeholder="Start Date"
          />
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            className="form-input"
            style={styles.filterInput}
            placeholder="End Date"
          />
          <button onClick={loadReports} className="btn btn-primary">
            Apply Filters
          </button>
          <button onClick={() => handleExport('json')} className="btn btn-secondary">
            Export JSON
          </button>
          <button onClick={() => handleExport('csv')} className="btn btn-secondary">
            Export CSV
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={styles.tabs}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="btn"
              style={{
                ...styles.tabButton,
                ...(activeTab === tab.id ? styles.tabButtonActive : {})
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {statistics && (
            <div className="card">
              <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                  <h3 style={styles.statLabel}>Total Tasks</h3>
                  <p style={styles.statValue}>{statistics.total || 0}</p>
                </div>
                <div style={styles.statCard}>
                  <h3 style={styles.statLabel}>Completed</h3>
                  <p style={styles.statValue}>{statistics.completed || 0}</p>
                </div>
                <div style={styles.statCard}>
                  <h3 style={styles.statLabel}>In Progress</h3>
                  <p style={styles.statValue}>{statistics.in_progress || 0}</p>
                </div>
                <div style={styles.statCard}>
                  <h3 style={styles.statLabel}>Pending</h3>
                  <p style={styles.statValue}>{statistics.pending || 0}</p>
                </div>
                {statistics.completion_rate && (
                  <div style={styles.statCard}>
                    <h3 style={styles.statLabel}>Completion Rate</h3>
                    <p style={styles.statValue}>
                      {statistics.completion_rate.toFixed(1)}%
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div style={styles.charts}>
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Tasks by Status</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {userActivityData.length > 0 && (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">User Activity</h3>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={userActivityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="created" fill="#2563eb" name="Created" />
                <Bar dataKey="completed" fill="#16a34a" name="Completed" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {timeline.length > 0 && (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Task Timeline</h3>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={timeline}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="created"
                  stroke="#2563eb"
                  name="Created"
                />
                <Line
                  type="monotone"
                  dataKey="completed"
                  stroke="#16a34a"
                  name="Completed"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
          </div>
        </>
      )}

      {/* User Performance Tab */}
      {activeTab === 'user-performance' && (
        <>
          <div className="card">
            <h3 className="card-title">Tasks Resolved by User</h3>
            {tasksResolvedByUser.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={tasksResolvedByUser}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="username" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="tasks_resolved" fill="#16a34a" name="Tasks Resolved" />
                    <Bar dataKey="tasks_allocated" fill="#2563eb" name="Tasks Allocated" />
                  </BarChart>
                </ResponsiveContainer>
                <div style={styles.tableContainer}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Tasks Resolved</th>
                        <th>Tasks Allocated</th>
                        <th>Avg Completion Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasksResolvedByUser.map((user) => (
                        <tr key={user.id}>
                          <td>{user.username}</td>
                          <td>{user.tasks_resolved || 0}</td>
                          <td>{user.tasks_allocated || 0}</td>
                          <td>
                            {user.avg_completion_time_minutes
                              ? formatTime(user.avg_completion_time_minutes)
                              : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p style={styles.empty}>No data available</p>
            )}
          </div>

          <div className="card">
            <h3 className="card-title">User Productivity Metrics</h3>
            {userProductivity.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={userProductivity}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="username" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="completed_tasks" fill="#16a34a" name="Completed" />
                    <Bar dataKey="in_progress_tasks" fill="#f59e0b" name="In Progress" />
                    <Bar dataKey="pending_tasks" fill="#64748b" name="Pending" />
                  </BarChart>
                </ResponsiveContainer>
                <div style={styles.tableContainer}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Total Tasks</th>
                        <th>Completed</th>
                        <th>In Progress</th>
                        <th>Pending</th>
                        <th>Total Time Spent</th>
                        <th>Avg Time/Task</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userProductivity.map((user) => (
                        <tr key={user.id}>
                          <td>{user.username}</td>
                          <td>{user.total_tasks || 0}</td>
                          <td>{user.completed_tasks || 0}</td>
                          <td>{user.in_progress_tasks || 0}</td>
                          <td>{user.pending_tasks || 0}</td>
                          <td>{formatTime(user.total_time_spent_minutes || 0)}</td>
                          <td>
                            {user.avg_time_per_completed_task_minutes
                              ? formatTime(user.avg_time_per_completed_task_minutes)
                              : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p style={styles.empty}>No data available</p>
            )}
          </div>
        </>
      )}

      {/* Time Analytics Tab */}
      {activeTab === 'time-analytics' && (
        <>
          <div className="card">
            <h3 className="card-title">Time Spent by User</h3>
            {timeSpentByUser.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={timeSpentByUser}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="username" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatTime(value)} />
                    <Legend />
                    <Bar dataKey="total_time_minutes" fill="#2563eb" name="Total Time" />
                  </BarChart>
                </ResponsiveContainer>
                <div style={styles.tableContainer}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Total Time Spent</th>
                        <th>Log Entries</th>
                        <th>Tasks Worked On</th>
                        <th>Avg Time/Log</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timeSpentByUser.map((user) => (
                        <tr key={user.id}>
                          <td>{user.username}</td>
                          <td>{formatTime(user.total_time_minutes || 0)}</td>
                          <td>{user.log_entries || 0}</td>
                          <td>{user.tasks_worked_on || 0}</td>
                          <td>{formatTime(user.avg_time_per_log_minutes || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p style={styles.empty}>No data available</p>
            )}
          </div>
        </>
      )}

      {/* Task Type Analytics Tab */}
      {activeTab === 'task-type-analytics' && (
        <>
          <div className="card">
            <h3 className="card-title">Time Spent by Task Type</h3>
            {timeSpentByTaskType.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={timeSpentByTaskType} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="task_type" type="category" width={200} />
                    <Tooltip formatter={(value) => formatTime(value)} />
                    <Legend />
                    <Bar dataKey="total_time_minutes" fill="#2563eb" name="Total Time" />
                  </BarChart>
                </ResponsiveContainer>
                <div style={styles.tableContainer}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Task Type</th>
                        <th>Task Count</th>
                        <th>Completed</th>
                        <th>Total Time Spent</th>
                        <th>Avg Time/Log</th>
                        <th>Avg Completion Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timeSpentByTaskType.map((type, index) => (
                        <tr key={index}>
                          <td>{type.task_type || 'N/A'}</td>
                          <td>{type.task_count || 0}</td>
                          <td>{type.completed_count || 0}</td>
                          <td>{formatTime(type.total_time_minutes || 0)}</td>
                          <td>{formatTime(type.avg_time_per_log_minutes || 0)}</td>
                          <td>
                            {type.avg_completion_time_minutes
                              ? formatTime(type.avg_completion_time_minutes)
                              : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p style={styles.empty}>No data available</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const styles = {
  filters: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
  },
  filterInput: {
    maxWidth: '200px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '1.5rem',
  },
  statCard: {
    textAlign: 'center',
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
  },
  statLabel: {
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#64748b',
    margin: '0 0 0.5rem 0',
  },
  statValue: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#2563eb',
    margin: 0,
  },
  charts: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
    marginTop: '1.5rem',
  },
  loading: {
    textAlign: 'center',
    padding: '4rem 2rem',
    color: '#64748b',
  },
  tabs: {
    display: 'flex',
    gap: '0.5rem',
    borderBottom: '1px solid #e5e7eb',
    paddingBottom: '0.5rem',
  },
  tabButton: {
    padding: '0.75rem 1.5rem',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#64748b',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    borderRadius: '0.5rem 0.5rem 0 0',
  },
  tabButtonActive: {
    color: '#2563eb',
    backgroundColor: '#eff6ff',
    borderBottom: '2px solid #2563eb',
  },
  tableContainer: {
    marginTop: '1.5rem',
    overflowX: 'auto',
  },
  empty: {
    textAlign: 'center',
    padding: '2rem',
    color: '#64748b',
    fontStyle: 'italic',
  },
};

export default AdminReports;
